import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { ArrowLeft, Lock, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_91be65d6-9fee-4eda-abde-3fd83cab62ae/artifacts/kxr3j7eu_IMG_7793.png";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      try {
        const response = await axios.get(`${API}/auth/verify-reset-token/${token}`);
        setTokenValid(true);
        setEmail(response.data.email);
      } catch (error) {
        setTokenValid(false);
        setTokenError(error.response?.data?.detail || 'Invalid or expired reset link');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    try {
      setLoading(true);
      await axios.post(`${API}/auth/reset-password`, { 
        token, 
        password 
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-tiffany-500 font-mono">VERIFYING LINK...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="BLK MRKT" className="h-8 w-auto" />
          </Link>
          <Link 
            to="/login"
            className="text-sm font-mono text-tiffany-500 hover:underline"
          >
            LOGIN
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Back link */}
          <Link 
            to="/login"
            className="inline-flex items-center gap-2 text-zinc-500 hover:text-tiffany-500 font-mono text-sm mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            BACK TO LOGIN
          </Link>

          {!tokenValid ? (
            /* Invalid Token State */
            <div className="border border-zinc-800 bg-zinc-900/50 p-8 text-center">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
              <h1 className="font-sans text-2xl text-white mb-4">INVALID LINK</h1>
              <p className="text-zinc-400 font-mono text-sm mb-6">
                {tokenError}
              </p>
              <Link to="/forgot-password">
                <Button 
                  className="w-full h-12 bg-tiffany-500 text-black hover:bg-tiffany-400 font-sans tracking-widest rounded-none"
                  data-testid="request-new-link-btn"
                >
                  REQUEST NEW LINK
                </Button>
              </Link>
            </div>
          ) : success ? (
            /* Success State */
            <div className="border border-zinc-800 bg-zinc-900/50 p-8 text-center">
              <CheckCircle className="w-16 h-16 text-tiffany-500 mx-auto mb-6" />
              <h1 className="font-sans text-2xl text-white mb-4">PASSWORD RESET</h1>
              <p className="text-zinc-400 font-mono text-sm mb-8">
                Your password has been reset successfully. You can now login with your new password.
              </p>
              <Button 
                onClick={() => navigate('/login')}
                className="w-full h-12 bg-tiffany-500 text-black hover:bg-tiffany-400 font-sans tracking-widest rounded-none"
                data-testid="go-to-login-btn"
              >
                GO TO LOGIN
              </Button>
            </div>
          ) : (
            /* Form State */
            <div className="border border-zinc-800 bg-zinc-900/50 p-8">
              <div className="flex items-center gap-3 mb-6">
                <Lock className="w-6 h-6 text-tiffany-500" />
                <h1 className="font-sans text-2xl text-white">RESET PASSWORD</h1>
              </div>
              
              <p className="text-zinc-400 font-mono text-sm mb-2">
                Create a new password for
              </p>
              <p className="text-tiffany-500 font-mono text-sm mb-8">
                {email}
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-zinc-500 font-mono text-xs uppercase tracking-wider mb-2">
                    NEW PASSWORD
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Min. 8 characters"
                      className="w-full h-12 bg-zinc-900 border-zinc-800 text-white font-mono focus:border-tiffany-500 rounded-none pr-12"
                      data-testid="reset-password-input"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-zinc-500 font-mono text-xs uppercase tracking-wider mb-2">
                    CONFIRM PASSWORD
                  </label>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter password"
                    className="w-full h-12 bg-zinc-900 border-zinc-800 text-white font-mono focus:border-tiffany-500 rounded-none"
                    data-testid="reset-confirm-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading || password.length < 8 || password !== confirmPassword}
                  className="w-full h-12 bg-tiffany-500 text-black hover:bg-tiffany-400 font-sans tracking-widest rounded-none disabled:opacity-50"
                  data-testid="reset-submit-btn"
                >
                  {loading ? 'RESETTING...' : 'RESET PASSWORD'}
                </Button>
              </form>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
